import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { useToast } from '../../../components/ui/use-toast';
import { Loader2, ArrowLeft, DollarSign, CreditCard, Wallet, TrendingUp, Users, Vote } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

interface ClientFinancesProps {}

interface FinancialData {
  userName: string;
  totalCollected: number;
  partnerBalance: number;
  votAfricaCommission: number;
  totalWithdrawals: number;
  totalVotes: number;
  totalContestants: number;
  paymentMethods: {
    method: string;
    amount: number;
    percentage: number;
  }[];
  transactionHistory: {
    date: string;
    amount: number;
  }[];
  withdrawalHistory: {
    date: string;
    amount: number;
    status: string;
  }[];
  contests: {
    id: string;
    name: string;
    slug: string;
    revenue: number;
    commission: number;
    commissionPercentage: number;
    votes: number;
  }[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Fonction pour calculer le revenu ajusté - identique à celle dans Dashboard.tsx
const calculateAdjustedRevenue = (rawRevenue: number, transactionsCount: number) => {
  // Calculer le taux de charge basé sur le nombre de transactions et le revenu total
  const T_eff = Math.max(transactionsCount || 30, 30);
  const charge_rate = Math.min(10, 
    3 + // taux de base
    5 * Math.sqrt(T_eff / 500) + // composante transactions
    5 * (Math.log(rawRevenue + 1) / Math.log(1000001)) // composante revenu
  );

  // Calculer le revenu ajusté après application des charges
  const adjusted_revenue = rawRevenue;
  
  // Arrondir au multiple de 100 le plus proche pour plus de discrétion
  return Math.round(adjusted_revenue / 100) * 100;
};

const ClientFinances: React.FC<ClientFinancesProps> = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useCheckPermissions(['admin']);
  const [isLoading, setIsLoading] = useState(true);
  const [financialData, setFinancialData] = useState<FinancialData | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchClientFinances = async () => {
      if (!id) return;

      try {
        setIsLoading(true);

        // Récupérer les informations de base du client
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, name, email, permissions')
          .eq('id', id)
          .single();

        if (userError) throw userError;

        // Vérifier si l'utilisateur a des permissions de concours
        if (!userData.permissions?.contests || userData.permissions.contests.length === 0) {
          setFinancialData({
            userName: userData.name,
            totalCollected: 0,
            partnerBalance: 0,
            votAfricaCommission: 0,
            totalWithdrawals: 0,
            totalVotes: 0,
            totalContestants: 0,
            paymentMethods: [],
            transactionHistory: [],
            withdrawalHistory: [],
            contests: []
          });
          setIsLoading(false);
          return;
        }

        const contestIds = userData.permissions.contests;

        // Récupérer les statistiques des concours avec les transactions
        const { data: contestsRevenue, error: contestsError } = await supabase
          .rpc('get_contests_revenue');

        if (contestsError) {
          console.error('Erreur lors de la récupération des revenus des concours:', contestsError);
          throw contestsError;
        }

        // Filtrer les concours pour n'inclure que ceux du client
        const userContestsRevenue = contestsRevenue?.filter(contest => 
          contestIds.includes(contest.id)
        ) || [];

        // Récupérer les détails des concours
        const { data: contestStats, error } = await supabase
          .from('contests')
          .select(`
            id,
            name,
            slug,
            commission_percentage,
            contestants (
              id,
              name
            )
          `)
          .in('id', contestIds);

        if (error) {
          console.error('Error fetching contest stats:', error);
          throw error;
        }

        // Récupérer les transactions pour ces concours
        const { data: transactionsData, error: transactionsError } = await supabase
          .from('transactions')
          .select(`
            id,
            amount,
            created_at,
            payment_method,
            status,
            contestant_id
          `)
          .in('contestant_id', contestStats.flatMap(contest => contest.contestants.map(c => c.id)))
          .eq('status', 'completed');

        if (transactionsError) throw transactionsError;

        // Récupérer les retraits effectués par ce client
        const { data: withdrawalsData, error: withdrawalsError } = await supabase
          .from('withdrawals')
          .select('amount, created_at, status')
          .eq('user_id', id);

        if (withdrawalsError) throw withdrawalsError;

        // Calculer les statistiques financières exactement comme dans Dashboard.tsx
        let totalRevenue = 0;
        let totalCommission = 0;
        
        // Récupérer les statistiques des concours
        const formattedStats = await Promise.all(contestStats.map(async contest => {
          const revenueData = userContestsRevenue.find(c => c.id === contest.id);
          const rawRevenue = Number(revenueData?.total_revenue) || 0;
          const transactionsCount = Number(revenueData?.transactions_count) || 0;
          
          // Calculer le revenu ajusté pour ce concours spécifique
          const adjustedRevenue = calculateAdjustedRevenue(rawRevenue, transactionsCount);
          
          // Récupérer le nombre total de votes pour ce concours
          const contestantIds = contest.contestants.map(c => c.id);
          const { count: totalVotes } = await supabase
            .from('votes')
            .select('id', { count: 'exact', head: true })
            .in('contestant_id', contestantIds);
            
          return {
            id: contest.id,
            name: contest.name,
            slug: contest.slug || '',
            total_revenue: adjustedRevenue,
            total_votes: totalVotes || 0,
            contestants_count: contest.contestants.length,
            commission_percentage: contest.commission_percentage || 10,
            raw_revenue: rawRevenue
          };
        }));
        
        // Calculer les statistiques totales comme dans Dashboard.tsx
        const totals = formattedStats.reduce((acc, contest) => {
          const commission = (contest.total_revenue * contest.commission_percentage) / 100;
          
          return {
            total_revenue: acc.total_revenue + contest.total_revenue,
            total_votes: acc.total_votes + contest.total_votes,
            total_contestants: acc.total_contestants + contest.contestants_count,
            total_commission: acc.total_commission + commission,
          };
        }, { 
          total_revenue: 0, 
          total_votes: 0, 
          total_contestants: 0, 
          total_commission: 0,
        });
        
        totalRevenue = totals.total_revenue;
        totalCommission = totals.total_commission;
        
        const totalWithdrawals = withdrawalsData
  .filter(w => w.status !== 'cancelled' && w.status !== 'rejected')
  .reduce((sum, w) => sum + Number(w.amount), 0);
        const partnerBalance = totalRevenue - totalCommission - totalWithdrawals;
        
        // Analyser les méthodes de paiement comme dans Dashboard.tsx
        let paymentStats = { mobile_money: { amount: 0, count: 0 }, card: { amount: 0, count: 0 } };
        
        // Récupérer les données de paiement pour chaque concours
        for (const contest of contestStats) {
          const contestantIds = contest.contestants.map(c => c.id);
          
          // Récupérer les statistiques de paiement pour ce concours
          const { data: paymentData } = await supabase
            .from('transactions')
            .select('payment_method, amount')
            .in('contestant_id', contestantIds)
            .eq('status', 'completed');
            
          // Calculer la ventilation par méthode de paiement
          paymentData?.forEach(tx => {
            const method = tx.payment_method;
            const amount = typeof tx.amount === 'string' ? parseFloat(tx.amount) : tx.amount;
            if (method === 'mobile_money' || method === 'card') {
              if (!paymentStats[method]) {
                paymentStats[method] = { amount: 0, count: 0 };
              }
              paymentStats[method].amount += amount || 0;
              paymentStats[method].count++;
            }
          });
        }
        
        // Calculer les pourcentages
        const totalAmount = Object.values(paymentStats).reduce((sum, stat) => sum + stat.amount, 0);
        Object.keys(paymentStats).forEach(method => {
          paymentStats[method].percentage = (paymentStats[method].amount / totalAmount) * 100;
        });
        
        // Formater pour l'affichage
        const formattedPaymentMethods = [
          {
            method: 'Mobile Money',
            amount: paymentStats.mobile_money?.amount || 0,
            percentage: paymentStats.mobile_money?.percentage || 0
          },
          {
            method: 'PayPal / Carte bancaire',
            amount: paymentStats.card?.amount || 0,
            percentage: paymentStats.card?.percentage || 0
          }
        ];

        // Préparer l'historique des transactions par date
        const txByDate: Record<string, number> = {};
        transactionsData.forEach(tx => {
          const date = new Date(tx.created_at).toISOString().split('T')[0];
          txByDate[date] = (txByDate[date] || 0) + Number(tx.amount);
        });

        const transactionHistory = Object.entries(txByDate)
          .map(([date, amount]) => ({ date, amount }))
          .sort((a, b) => a.date.localeCompare(b.date));

        // Préparer l'historique des retraits
        const withdrawalHistory = withdrawalsData.map(w => ({
          date: new Date(w.created_at).toISOString().split('T')[0],
          amount: Number(w.amount),
          status: w.status || 'completed' // Utiliser 'completed' comme valeur par défaut si status est null
        })).sort((a, b) => a.date.localeCompare(b.date));

        // Compter le nombre total de votes
        const { count: totalVotes, error: votesError } = await supabase
          .from('votes')
          .select('id', { count: 'exact', head: true })
          .in('contestant_id', contestStats.flatMap(contest => contest.contestants.map(c => c.id)));

        if (votesError) throw votesError;

        // Préparer les données des concours
        const contestsData = await Promise.all(contestStats.map(async contest => {
          const revenueData = userContestsRevenue.find(c => c.id === contest.id);
          const rawRevenue = Number(revenueData?.total_revenue) || 0;
          const transactionsCount = Number(revenueData?.transactions_count) || 0;
          
          // Calculer le revenu ajusté comme dans Dashboard.tsx
          const adjustedRevenue = calculateAdjustedRevenue(rawRevenue, transactionsCount);
          
          const commissionPercentage = contest.commission_percentage || 10;
          const commission = (adjustedRevenue * commissionPercentage) / 100;
          
          // Compter les votes pour ce concours comme dans Dashboard.tsx
          const contestantIds = contest.contestants.map(c => c.id);
          
          // Utiliser la même requête que dans Dashboard.tsx pour compter les votes
          const { count: totalVotes, error: votesError } = await supabase
            .from('votes')
            .select('id', { count: 'exact', head: true })
            .in('contestant_id', contestantIds);
            
          if (votesError) {
            console.error("Erreur lors du comptage des votes:", votesError);
          }
          
          return {
            id: contest.id,
            name: contest.name,
            slug: contest.slug || '',
            revenue: adjustedRevenue,
            commission: commission,
            commissionPercentage: commissionPercentage,
            votes: totalVotes || 0
          };
        }));

        setFinancialData({
          userName: userData.name,
          totalCollected: totalRevenue,
          partnerBalance,
          votAfricaCommission: totalCommission,
          totalWithdrawals,
          totalVotes: totals.total_votes,
          totalContestants: totals.total_contestants,
          paymentMethods: formattedPaymentMethods,
          transactionHistory,
          withdrawalHistory,
          contests: contestsData
        });

      } catch (error) {
        console.error('Erreur lors de la récupération des données financières:', error);
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de récupérer les informations financières'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientFinances();
  }, [id, toast]);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex h-[80vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-yellow-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!financialData) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
          <p className="text-xl text-white">Aucune donnée financière disponible</p>
          <Button asChild variant="outline">
            <Link to="/admin/clients">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à la liste des clients
            </Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Finances de {financialData.userName}</h1>
            <p className="text-white">Vue d'ensemble des données financières du partenaire</p>
          </div>
          <Button asChild variant="outline">
            <Link to="/admin/clients">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à la liste
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white">Solde Partenaire</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Wallet className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-white">{formatCurrency(financialData.partnerBalance)}</span>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Collecté</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-2xl font-bold text-white">{formatCurrency(financialData.totalCollected)}</span>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white">Commission VotAfrica</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 text-yellow-400 mr-2" />
                <span className="text-2xl font-bold text-white">{formatCurrency(financialData.votAfricaCommission)}</span>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Retraits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <CreditCard className="h-5 w-5 text-purple-500 mr-2" />
                <span className="text-2xl font-bold text-white">{formatCurrency(financialData.totalWithdrawals)}</span>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Votes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Vote className="h-5 w-5 text-pink-500 mr-2" />
                <span className="text-2xl font-bold text-white">{financialData.totalVotes.toLocaleString('fr-FR')}</span>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Candidats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-indigo-500 mr-2" />
                <span className="text-2xl font-bold text-white">{financialData.totalContestants.toLocaleString('fr-FR')}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-800 border-gray-700 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4 text-white">Répartition des méthodes de paiement</h3>
            
            {financialData.paymentMethods.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={financialData.paymentMethods}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      fill="#8884d8"
                      dataKey="amount"
                    >
                      {[
                        { name: 'Mobile Money', color: '#fbbf24' },
                        { name: 'PayPal / Carte Bancaire', color: '#60a5fa' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: 'white' }}
                      labelStyle={{ color: 'white' }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry, index) => (
                        <span style={{ color: 'white' }}>
                          {financialData.paymentMethods[index]?.method}: {financialData.paymentMethods[index]?.percentage.toFixed(1)}%
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-white text-center py-10">Aucune donnée de paiement disponible</p>
            )}
          </div>

          <div className="bg-gray-800 border-gray-700 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4 text-white">Historique des transactions</h3>
            
            {financialData.transactionHistory.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={financialData.transactionHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
                    <XAxis dataKey="date" stroke="#e5e7eb" />
                    <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} stroke="#e5e7eb" />
                    <Tooltip 
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: 'white' }}
                      labelStyle={{ color: 'white' }}
                    />
                    <Line type="monotone" dataKey="amount" stroke="#FFD700" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-white text-center py-10">Aucune transaction disponible</p>
            )}
          </div>

          <div className="bg-gray-800 border-gray-700 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4 text-white">Historique des retraits</h3>
            
            {financialData.withdrawalHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="px-4 py-3 text-left text-sm font-medium text-white">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white">Montant</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {financialData.withdrawalHistory.map((withdrawal, index) => (
                      <tr key={index} className="hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-white">{new Date(withdrawal.date).toLocaleDateString('fr-FR')}</td>
                        <td className="px-4 py-3 text-sm text-white">{formatCurrency(withdrawal.amount)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            withdrawal.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            withdrawal.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            withdrawal.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-green-500/20 text-green-400' // Par défaut, on considère que c'est complété
                          }`}>
                            {withdrawal.status === 'completed' ? 'Complété' :
                             withdrawal.status === 'pending' ? 'En attente' :
                             withdrawal.status === 'cancelled' ? 'Annulé' :
                             'Complété'} {/* Par défaut, on affiche "Complété" */}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-white text-center py-10">Aucun retrait disponible</p>
            )}
          </div>
        </div>
        
        {/* Liste des concours */}
        <div className="bg-gray-800 border-gray-700 rounded-2xl p-6">
          <h3 className="text-xl font-bold mb-6 text-white">Concours du partenaire</h3>
          
          {financialData.contests.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {financialData.contests.map((contest) => (
                <div
                  key={contest.id}
                  className="bg-gray-700/50 border border-gray-600 rounded-xl p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-white truncate max-w-[70%]">
                      {contest.name}
                    </h4>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-300">Revenus</p>
                      <p className="text-xl font-bold text-white">
                        {formatCurrency(contest.revenue)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">Commission VotAfrica</p>
                      <p className="text-xl font-bold text-white">
                        {formatCurrency(contest.commission)}
                        <span className="text-sm ml-1">({contest.commissionPercentage}%)</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">Votes</p>
                      <p className="text-xl font-bold text-white">
                        {contest.votes.toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white text-center py-10">Aucun concours disponible</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ClientFinances;
